import { createContext, FC, useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { getAuthToken, setAuthToken } from '../services/auth';
import { useJurisdiction } from '../hooks';
import { LoadingFullScreen } from '../components/loading';

export type Auth = {
    isPasswordVerified: boolean;
    isEmailVerified: boolean;
    isPhoneVerified: boolean;
    password: string
    email: string;
    phone: string;
    fetchAuthData: () => void;
};

const initialAppState: Auth = {
    isPasswordVerified: false,
    isEmailVerified: false,
    isPhoneVerified: false,
    password: '',
    email: '',
    phone: '',
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    fetchAuthData: () => {
    },
};

export type AuthData = {
    token: string;
    isEmailVerified: boolean;
    isPhoneVerified: boolean;
    isPasswordVerified: boolean;
    email: string;
    phone: string;
    password: string;
};

export type AuthDataResponse = {
    success: boolean;
    isCreated: boolean;
    data: AuthData;
};

export const AuthContext = createContext<Auth>(initialAppState);

export const useAuth = () => useContext(AuthContext);

export const AuthContextProvider: FC = ({ children }) => {
    const { slug } = useJurisdiction();

    const [auth, setAuth] = useState<Auth>(initialAppState);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [trigger, setTrigger] = useState({});

    const fetchAuthData = useCallback(() => setTrigger({}), []);

    useEffect(() => {
        const controller = new AbortController();

        (async () => {
            try {
                setIsLoading(true);

                const { data } = await axios.post<AuthDataResponse>(
                    `https://www.safetynowalerts.com/api/v1/request/${slug}/data`,
                    {
                        token: getAuthToken(slug),
                    }, {
                        signal: controller.signal,
                    }
                );

                const { isCreated, data: authData } = data;
                const {
                    token,
                    isEmailVerified,
                    isPhoneVerified,
                    isPasswordVerified,
                    email,
                    phone,
                    password,
                } = authData;

                if (isCreated) {
                    setAuthToken(slug, token);
                }

                setAuth(prevState => ({
                    ...prevState,
                    isEmailVerified,
                    isPhoneVerified,
                    isPasswordVerified,
                    email,
                    phone,
                    password,
                }));
            } catch (e) {
                console.error(e);
                // window.location.reload();
            }

            setIsLoading(false);
        })();

        return () => {
            controller.abort();
        };
    }, [slug, trigger]);

    if (isLoading) {
        return (
            <LoadingFullScreen />
        );
    }

    return (
        <AuthContext.Provider
            value={{
                ...auth,
                fetchAuthData,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};
